/* eslint-disable no-console */
/* eslint-disable no-param-reassign */

'use client';

import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { Key } from 'react-aria';
import type { LayoutInfo, Rect, ScrollPadding } from '../layouts/types';
import type { ListFilterState } from '../List/types';
import { useCachedValue } from './useCachedValue';

export type ScrollAlignment = 'start' | 'center' | 'end';

export type ScrollCompleteInfo = {
  key: Key;
  node: any;
  didScroll: boolean;
  element?: Element;
  type: 'smooth' | 'immediate';
};

export type OnScrollCompleteCallback = (info: ScrollCompleteInfo) => void;

type UseScrollToProps = {
  layoutInfo: Map<Key, LayoutInfo>;
  visibleRect: Rect;
  totalSize: number;
  onScrollComplete?: OnScrollCompleteCallback;
  scrollPadding?: ScrollPadding;
  timeoutRef?: MutableRefObject<NodeJS.Timeout | null>;
};

/**
 * A hook that provide a function to immediately scroll to a key,
 * with support for variable item sizes.
 *
 * @example
 * const scrollTo = useScrollTo(
 *  ref, props, state
 * );
 *
 * function onClick() {
 *  scrollTo(key);
 * }
 */

export function useScrollTo<T>(
  scrollRef: React.RefObject<HTMLElement | null>,
  {
    layoutInfo,
    visibleRect,
    onScrollComplete,
    scrollPadding,
    timeoutRef,
  }: UseScrollToProps,
  state?: ListFilterState<T>
) {
  const internalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeout = timeoutRef || internalTimeoutRef;

  const collection = state?.collection;

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [timeout]);

  const cache = useCachedValue({
    layoutInfo,
    visibleRect,
    collection,
  });

  return (
    key: Key,
    alignmentProp: ScrollAlignment | 'auto' = 'start',
    { force = false } = {}
  ) => {
    // @ts-expect-error FIXME(strictNullChecks)
    clearTimeout(timeout.current);
    timeout.current = null;

    // @ts-expect-error FIXME(strictNullChecks)
    const layout = cache.current.layoutInfo.get(key);

    if (!layout) {
      console.warn(`Attempting to scroll to missing key: ${key}`);
      return;
    }

    function onComplete(didScroll: boolean) {
      onScrollComplete?.({
        didScroll,
        node: collection?.getItem(key)?.value,
        key,
        // @ts-expect-error FIXME(strictNullChecks)
        element: scrollRef.current.querySelector(`[data-key="${key}"]`),
        type: 'immediate',
      });
    }

    if (!force && isItemInView(key, layoutInfo, visibleRect, scrollPadding)) {
      onComplete(false);
      return;
    }

    let alignment = alignmentProp;

    if (alignment === 'auto') {
      // @ts-expect-error FIXME(strictNullChecks)
      const targetY = cache.current.layoutInfo.get(key)?.rect.y;
      alignment =
        // @ts-expect-error FIXME(strictNullChecks)
        targetY - getScrollPaddingTop(key, layoutInfo) <= visibleRect.y
          ? 'start'
          : 'end';
    }

    function scroll() {
      // @ts-expect-error FIXME(strictNullChecks)
      clearTimeout(timeout.current);

      // @ts-expect-error FIXME(strictNullChecks)
      scrollRef.current.scrollTop = getScrollTopForAlignment(
        key,
        alignment as ScrollAlignment,
        // @ts-expect-error FIXME(strictNullChecks)
        cache.current.layoutInfo,
        // @ts-expect-error FIXME(strictNullChecks)
        cache.current.visibleRect,
        // @ts-expect-error FIXME(strictNullChecks)
        scrollRef.current.scrollHeight,
        scrollPadding
      );

      timeout.current = setTimeout(() => {
        if (
          approxEqual(
            // @ts-expect-error FIXME(strictNullChecks)
            cache.current.visibleRect.y,
            getScrollTopForAlignment(
              key,
              alignment as ScrollAlignment,
              // @ts-expect-error FIXME(strictNullChecks)
              cache.current.layoutInfo,
              // @ts-expect-error FIXME(strictNullChecks)
              cache.current.visibleRect,
              // @ts-expect-error FIXME(strictNullChecks)
              scrollRef.current.scrollHeight,
              scrollPadding
            )
          )
        ) {
          timeout.current = null;
          onComplete(true);
          return;
        }

        scroll();
      });
    }

    scroll();
  };
}

function approxEqual(a: number, b: number) {
  return Math.abs(a - b) < 1;
}

export function isItemInView(
  key: Key,
  layout: Map<Key, LayoutInfo>,
  visibleRect: Rect,
  scrollPadding: ScrollPadding = { top: 0, bottom: 0 }
) {
  const scrollPaddingTop = Math.max(
    getScrollPaddingTop(key, layout),
    scrollPadding.top
  );
  const itemLayout = layout.get(key);
  const scrollPaddingBottom = scrollPadding.bottom;

  if (!itemLayout) {
    // eslint-disable-next-line no-console
    console.warn(`Attempting see if missing item is in: ${key}`);
    return false;
  }

  if (
    visibleRect.y + scrollPaddingTop > itemLayout.rect.y ||
    visibleRect.y + visibleRect.height - scrollPaddingBottom <
      itemLayout.rect.y + itemLayout.rect.height
  ) {
    return false;
  }

  return true;
}

export function getScrollTopForAlignment(
  key: Key,
  alignment: 'start' | 'center' | 'end',
  layout: Map<Key, LayoutInfo>,
  visibleRect: Rect,
  totalSize: number,
  scrollPadding: ScrollPadding = { top: 0, bottom: 0 }
) {
  // @ts-expect-error FIXME(strictNullChecks)
  const itemRect = layout.get(key).rect;

  let toOffset: number;

  if (alignment === 'start') {
    // compensate for sticky headers
    toOffset =
      itemRect.y -
      Math.max(scrollPadding.top, getScrollPaddingTop(key, layout));
  } else if (alignment === 'center') {
    toOffset = itemRect.y - (visibleRect.height - itemRect.height) / 2;
  } else if (alignment === 'end') {
    toOffset =
      itemRect.y -
      (visibleRect.height - scrollPadding.bottom - itemRect.height);
  }

  const maxOffset = totalSize - visibleRect.height;
  // @ts-expect-error FIXME(strictNullChecks)
  return Math.max(Math.min(maxOffset, toOffset), 0);
}

export function getScrollPaddingTop(
  key: Key,
  layoutInfo: Map<Key, LayoutInfo>
) {
  const parentKey = layoutInfo.get(key)?.parentKey;
  let scrollPaddingTop = 0;

  if (parentKey) {
    const parent = layoutInfo.get(parentKey);
    // @ts-expect-error FIXME(strictNullChecks)
    if (parent.type === 'section' && parent.item && parent.item.isSticky) {
      // @ts-expect-error FIXME(strictNullChecks)
      scrollPaddingTop = parent.item.rect.height;
    }
  }

  return scrollPaddingTop;
}
